import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Building2, User, Upload, Check } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // Start from step 1 (account type)
  const [accountType, setAccountType] = useState('');
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [formData, setFormData] = useState({
    // Private
    tax_code: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'IT',
    state: '',
    // Company
    company_name: '',
    vat_number: '',
    sdi_code: '',
    pec: '',
    // Referente Eventi
    event_manager_name: '',
    event_manager_email: '',
    event_manager_phone: ''
  });
  const [loading, setLoading] = useState(false);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Converti in base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogo(reader.result);
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Upload logo first if present
      if (logo) {
        await axios.patch(`${API_URL}/tenant/logo`, {
          logo_base64: logo
        });
      }

      // Complete onboarding
      await axios.post(`${API_URL}/tenant/onboarding`, {
        account_type: accountType,
        ...formData
      });

      toast.success('Profilo completato!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Completa il tuo profilo</h1>
          <p className="text-gray-600">Ultimi passi per iniziare</p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-2">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
              step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
            }`}>
              {step > 1 ? <Check className="w-5 h-5" /> : '1'}
            </div>
            <div className={`w-16 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
              step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
            }`}>
              {step > 2 ? <Check className="w-5 h-5" /> : '2'}
            </div>
            <div className={`w-16 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
              step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
            }`}>
              3
            </div>
          </div>
        </div>

        <Card>
          <CardContent className="p-8">
            {/* Step 1: Choose Account Type */}
            {step === 1 && (
              <div className="space-y-6" data-testid="step-account-type">
                <div className="text-center">
                  <h2 className="text-2xl font-semibold mb-2">Tipo di Account</h2>
                  <p className="text-gray-600 mb-6">Seleziona se sei un privato o un'azienda</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setAccountType('private')}
                    className={`p-6 border-2 rounded-xl hover:border-blue-500 transition-all ${
                      accountType === 'private' ? 'border-blue-600 bg-blue-50' : 'border-gray-200'
                    }`}
                    data-testid="account-type-private"
                  >
                    <User className="w-12 h-12 mx-auto mb-3 text-blue-600" />
                    <h3 className="font-semibold text-lg">Privato</h3>
                    <p className="text-sm text-gray-600 mt-2">Persona fisica</p>
                  </button>

                  <button
                    onClick={() => setAccountType('company')}
                    className={`p-6 border-2 rounded-xl hover:border-blue-500 transition-all ${
                      accountType === 'company' ? 'border-blue-600 bg-blue-50' : 'border-gray-200'
                    }`}
                    data-testid="account-type-company"
                  >
                    <Building2 className="w-12 h-12 mx-auto mb-3 text-blue-600" />
                    <h3 className="font-semibold text-lg">Azienda</h3>
                    <p className="text-sm text-gray-600 mt-2">Società o ditta</p>
                  </button>
                </div>

                <div className="flex gap-3 justify-end pt-4">
                  <Button onClick={() => setStep(2)} disabled={!accountType} data-testid="continue-to-logo-button">
                    Continua
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Upload Logo (Optional) */}
            {step === 2 && (
              <div className="space-y-6" data-testid="step-logo">
                <div className="text-center">
                  <Upload className="w-16 h-16 mx-auto mb-4 text-blue-600" />
                  <h2 className="text-2xl font-semibold mb-2">Carica il tuo Logo</h2>
                  <p className="text-gray-600 mb-6">Il tuo logo apparirà sui biglietti e nell'interfaccia (opzionale)</p>
                </div>

                <div className="flex flex-col items-center">
                  {logoPreview && (
                    <div className="mb-4">
                      <img src={logoPreview} alt="Logo preview" className="w-32 h-32 object-contain rounded-lg border-2 border-gray-200" />
                    </div>
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    data-testid="logo-upload-input"
                    className="max-w-md"
                  />
                  <p className="text-sm text-gray-500 mt-2">Formato consigliato: PNG o JPG, max 2MB</p>
                </div>

                <div className="flex gap-3 justify-between pt-4">
                  <Button onClick={() => setStep(1)} variant="outline" data-testid="back-to-account-type-button">
                    Indietro
                  </Button>
                  <div className="flex gap-2">
                    <Button onClick={() => setStep(3)} data-testid="skip-logo-button" variant="outline">
                      Salta per ora
                    </Button>
                    <Button onClick={() => setStep(3)} disabled={!logo} data-testid="continue-with-logo-button">
                      Continua
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Billing Data */}
            {step === 3 && (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="step-billing">
                <div className="text-center">
                  <h2 className="text-2xl font-semibold mb-2">Dati di Fatturazione</h2>
                  <p className="text-gray-600 mb-6">
                    {accountType === 'private' ? 'Inserisci i tuoi dati personali' : 'Inserisci i dati aziendali'}
                  </p>
                </div>

                {accountType === 'company' && (
                  <>
                    <div>
                      <Label htmlFor="company_name">Ragione Sociale *</Label>
                      <Input
                        id="company_name"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        required
                        data-testid="company-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="vat_number">Partita IVA *</Label>
                      <Input
                        id="vat_number"
                        name="vat_number"
                        value={formData.vat_number}
                        onChange={handleChange}
                        required
                        data-testid="vat-number-input"
                        placeholder="IT12345678901"
                      />
                    </div>
                  </>
                )}

                <div>
                  <Label htmlFor="tax_code">Codice Fiscale *</Label>
                  <Input
                    id="tax_code"
                    name="tax_code"
                    value={formData.tax_code}
                    onChange={handleChange}
                    required
                    data-testid="tax-code-input"
                  />
                </div>

                <div>
                  <Label htmlFor="address">Indirizzo *</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    data-testid="address-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">Città *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      data-testid="city-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">Stato/Provincia *</Label>
                    <Input
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                      data-testid="state-input"
                      placeholder="Es: MI, RM, TO"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postal_code">CAP *</Label>
                    <Input
                      id="postal_code"
                      name="postal_code"
                      value={formData.postal_code}
                      onChange={handleChange}
                      required
                      data-testid="postal-code-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Paese *</Label>
                    <select
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      required
                      data-testid="country-select"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <option value="IT">Italia</option>
                      <option value="FR">Francia</option>
                      <option value="DE">Germania</option>
                      <option value="ES">Spagna</option>
                      <option value="GB">Regno Unito</option>
                      <option value="CH">Svizzera</option>
                      <option value="AT">Austria</option>
                      <option value="US">Stati Uniti</option>
                      <option value="OTHER">Altro</option>
                    </select>
                  </div>
                </div>

                {accountType === 'company' && formData.country === 'IT' && (
                  <>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-900">
                        ℹ️ SDI e PEC sono richiesti solo per aziende italiane (fatturazione elettronica)
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="sdi_code">Codice SDI (Fatturazione Elettronica)</Label>
                      <Input
                        id="sdi_code"
                        name="sdi_code"
                        value={formData.sdi_code}
                        onChange={handleChange}
                        data-testid="sdi-code-input"
                        placeholder="7 caratteri"
                        maxLength={7}
                      />
                    </div>
                    <div>
                      <Label htmlFor="pec">PEC</Label>
                      <Input
                        id="pec"
                        name="pec"
                        type="email"
                        value={formData.pec}
                        onChange={handleChange}
                        data-testid="pec-input"
                        placeholder="esempio@pec.it"
                      />
                    </div>
                  </>
                )}

                {/* Referente Eventi */}
                <div className="border-t border-gray-200 pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Referente Eventi</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Persona di contatto per la gestione eventi
                  </p>

                  <div>
                    <Label htmlFor="event_manager_name">Nome Referente *</Label>
                    <Input
                      id="event_manager_name"
                      name="event_manager_name"
                      value={formData.event_manager_name}
                      onChange={handleChange}
                      required
                      data-testid="event-manager-name-input"
                      placeholder="Nome e cognome"
                    />
                  </div>

                  <div>
                    <Label htmlFor="event_manager_email">Email Referente *</Label>
                    <Input
                      id="event_manager_email"
                      name="event_manager_email"
                      type="email"
                      value={formData.event_manager_email}
                      onChange={handleChange}
                      required
                      data-testid="event-manager-email-input"
                      placeholder="referente@esempio.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="event_manager_phone">Telefono Referente *</Label>
                    <Input
                      id="event_manager_phone"
                      name="event_manager_phone"
                      type="tel"
                      value={formData.event_manager_phone}
                      onChange={handleChange}
                      required
                      data-testid="event-manager-phone-input"
                      placeholder="+39 XXX XXX XXXX"
                    />
                  </div>
                </div>

                <div className="flex gap-3 justify-between pt-4">
                  <Button type="button" onClick={() => setStep(2)} variant="outline" data-testid="back-to-account-type-button">
                    Indietro
                  </Button>
                  <Button type="submit" disabled={loading} data-testid="complete-onboarding-button">
                    {loading ? 'Salvataggio...' : 'Completa Profilo'}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
